import { toDayJs } from '~/services/TimeService';

import { ExistingBlackoutDate, StopsellApplyBlock, StopsellApplyRange, StopsellDateRange } from '~/types/stopsell';

export function mergeDateRanges(ranges: Array<StopsellDateRange>): Array<StopsellDateRange> {
  if (ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start));
  return sorted.reduce<Array<StopsellDateRange>>((acc, range) => {
    const prev = acc[acc.length - 1];
    if (!prev) return [{ ...range }];
    const nextStartsAfterGap = toDayJs(range.start).isAfter(toDayJs(prev.end).add(1, 'day'), 'day');
    if (nextStartsAfterGap) return [...acc, { ...range }];
    // prev is always a spread-copy owned by this reducer — mutation is safe
    prev.end = range.end > prev.end ? range.end : prev.end;
    return acc;
  }, []);
}

export function calculateReopenPayloadParts(
  mergedReopenRanges: Array<StopsellDateRange>,
  blackouts: Array<ExistingBlackoutDate>,
): { blocksToDelete: Array<StopsellApplyBlock>; rangesToCreate: Array<StopsellApplyRange> } {
  const blocksToDelete: Array<StopsellApplyBlock> = [];
  const rangesToCreate: Array<StopsellApplyRange> = [];

  for (const blackout of blackouts) {
    const overlapping = mergedReopenRanges.filter(
      (range) =>
        !toDayJs(range.end).isBefore(toDayJs(blackout.start), 'day') &&
        !toDayJs(range.start).isAfter(toDayJs(blackout.end), 'day'),
    );

    if (overlapping.length === 0) continue;

    blocksToDelete.push(...blackout.blocks);

    let remainders: Array<StopsellDateRange> = [{ start: blackout.start, end: blackout.end }];

    for (const reopenRange of overlapping) {
      const rs = toDayJs(reopenRange.start);
      const re = toDayJs(reopenRange.end);
      const next: Array<StopsellDateRange> = [];

      for (const segment of remainders) {
        const bs = toDayJs(segment.start);
        const be = toDayJs(segment.end);

        if (re.isBefore(bs, 'day') || rs.isAfter(be, 'day')) {
          next.push(segment);
          continue;
        }

        if (rs.isAfter(bs, 'day')) {
          next.push({ start: segment.start, end: rs.subtract(1, 'day').format('YYYY-MM-DD') });
        }

        if (re.isBefore(be, 'day')) {
          next.push({ start: re.add(1, 'day').format('YYYY-MM-DD'), end: segment.end });
        }
      }

      remainders = next;
    }

    // Fan out remainders per rate — each block corresponds to a rate that must be re-created
    const rateIds = [...new Set(blackout.blocks.map((b) => b.room_rate_id))];
    for (const fragment of remainders) {
      for (const roomRateId of rateIds) {
        rangesToCreate.push({ start: fragment.start, end: fragment.end, roomRateId });
      }
    }
  }

  return { blocksToDelete, rangesToCreate };
}
